/**
 * Local library DB initialization.
 *
 * Opens an `@libsql/client` SQLite connection over a `file:` URL, sets WAL +
 * FK pragmas, applies the one-shot Libraries migration if the legacy
 * `sources` table is present, then runs the modern DDL (idempotent via
 * CREATE TABLE IF NOT EXISTS). `@libsql/client` runs on both Node and Bun
 * from a single driver — no runtime dispatch.
 *
 * @docLink packages/library/concepts#local-library
 */

import * as fs from "node:fs";
import * as os from "node:os";
import * as path from "node:path";
import { type Client, createClient, type InStatement } from "@libsql/client";
import { drizzle, type LibSQLDatabase } from "drizzle-orm/libsql";
import * as schema from "./schema.js";
import { migrateSidecarsToStoreIfNeeded } from "./store-paths.js";

// ---------------------------------------------------------------------------
// Modern DDL — runs after migration; safe to run unconditionally because of
// CREATE TABLE IF NOT EXISTS semantics.
// ---------------------------------------------------------------------------

const DDL = `
CREATE TABLE IF NOT EXISTS libraries (
  id                  TEXT    PRIMARY KEY,
  name                TEXT    NOT NULL,
  path                TEXT    NOT NULL,
  backend             TEXT    NOT NULL,
  backend_config      TEXT    NOT NULL DEFAULT '{}',
  ownership           TEXT    NOT NULL,
  structure           TEXT,
  is_default          INTEGER NOT NULL DEFAULT 0,
  manifest_generated  INTEGER NOT NULL DEFAULT 0,
  last_sync_at        TEXT,
  created_at          TEXT NOT NULL,
  updated_at          TEXT NOT NULL
);
CREATE UNIQUE INDEX IF NOT EXISTS idx_libraries_name ON libraries(name);
CREATE UNIQUE INDEX IF NOT EXISTS idx_libraries_default
  ON libraries(is_default) WHERE is_default = 1;

CREATE TABLE IF NOT EXISTS asset_definitions (
  id                    TEXT PRIMARY KEY,
  kind                  TEXT NOT NULL,
  publisher             TEXT NOT NULL,
  name                  TEXT NOT NULL,
  version               TEXT NOT NULL,
  sha256                TEXT,
  description           TEXT,
  license               TEXT,
  category              TEXT,
  manifest              TEXT NOT NULL,
  library_id            TEXT REFERENCES libraries(id) ON DELETE SET NULL,
  soft_deps             TEXT,
  kind_provider_version TEXT,
  cached_at             TEXT NOT NULL,
  updated_at            TEXT NOT NULL
);
CREATE INDEX IF NOT EXISTS idx_asset_defs_kind        ON asset_definitions(kind);
CREATE INDEX IF NOT EXISTS idx_asset_defs_publisher   ON asset_definitions(publisher);
CREATE INDEX IF NOT EXISTS idx_asset_defs_library_id  ON asset_definitions(library_id);

CREATE TABLE IF NOT EXISTS instances (
  id                TEXT PRIMARY KEY,
  def_ref           TEXT NOT NULL,
  def_pin           TEXT NOT NULL,
  config            TEXT NOT NULL DEFAULT '{}',
  credential_ref    TEXT,
  created_at        TEXT NOT NULL,
  updated_at        TEXT NOT NULL,
  created_by        TEXT,
  source_commit_sha TEXT
);
CREATE INDEX IF NOT EXISTS idx_instances_def_ref ON instances(def_ref);

CREATE TABLE IF NOT EXISTS assignments (
  id             TEXT PRIMARY KEY,
  workspace_id   TEXT NOT NULL,
  instance_id    TEXT NOT NULL REFERENCES instances(id) ON DELETE CASCADE,
  pin_policy     TEXT NOT NULL,
  assigned_at    TEXT NOT NULL
);
CREATE UNIQUE INDEX IF NOT EXISTS idx_assignments_ws_instance
  ON assignments(workspace_id, instance_id);
CREATE INDEX IF NOT EXISTS idx_assignments_instance_id ON assignments(instance_id);
`;

// ---------------------------------------------------------------------------
// Migration — runs only if the legacy `sources` table is present and no
// `libraries` table exists yet. Idempotent: subsequent opens skip it.
// ---------------------------------------------------------------------------

async function legacySourcesPresent(client: Client): Promise<boolean> {
  const rs = await client.execute(
    "SELECT name FROM sqlite_master WHERE type='table' AND name='sources'",
  );
  return rs.rows.length > 0;
}

async function librariesPresent(client: Client): Promise<boolean> {
  const rs = await client.execute(
    "SELECT name FROM sqlite_master WHERE type='table' AND name='libraries'",
  );
  return rs.rows.length > 0;
}

/**
 * Convert an absolute path to a slug suitable for a unique library name.
 * "/home/u/code/skills" -> "skills"; collisions resolved at call site.
 */
function slugify(p: string): string {
  const base = path.basename(p) || "lib";
  return (
    base
      .toLowerCase()
      .replace(/[^a-z0-9-]+/g, "-")
      .replace(/^-|-$/g, "") || "lib"
  );
}

interface OldSourceRow {
  id: string;
  type: string;
  url: string;
  path: string | null;
  ref: string | null;
  last_synced_at: string | null;
  created_at: string;
}

/**
 * One-shot `sources` → `libraries` migration. Builds the full statement set,
 * then applies it as a single transactional `client.batch` so a partial
 * failure rolls back cleanly.
 */
async function runLibrariesMigration(client: Client): Promise<void> {
  // Read the legacy rows first — the backfill statements depend on them.
  const oldRows = (
    await client.execute("SELECT id, type, url, path, ref, last_synced_at, created_at FROM sources")
  ).rows as unknown as OldSourceRow[];

  const stmts: InStatement[] = [];

  // 1. Create the new libraries table (without the UNIQUE index yet — we set
  //    the name during backfill, then add the index).
  stmts.push({
    sql: `
    CREATE TABLE libraries (
      id                  TEXT PRIMARY KEY,
      name                TEXT NOT NULL,
      path                TEXT NOT NULL,
      backend             TEXT NOT NULL,
      backend_config      TEXT NOT NULL DEFAULT '{}',
      ownership           TEXT NOT NULL,
      structure           TEXT,
      is_default          INTEGER NOT NULL DEFAULT 0,
      manifest_generated  INTEGER NOT NULL DEFAULT 0,
      last_sync_at        TEXT,
      created_at          TEXT NOT NULL,
      updated_at          TEXT NOT NULL
    );
  `,
  });

  // 2. Backfill from sources. `type` was github|local|catalog; we map to
  //    backend = git|local. The `catalog` (a.k.a. "store") backend was retired
  //    in the 2026-05-27 vocabulary cleanup — those rows migrate to `git`
  //    with the catalog URL kept as the git url, which is the closest
  //    backwards-compatible mapping.
  const usedNames = new Set<string>();
  for (const row of oldRows) {
    const backend = row.type === "github" || row.type === "catalog" ? "git" : "local";
    const ownership = backend === "local" ? "owner" : "reader";
    const baseSlug = slugify(row.path ?? row.url);
    let name = baseSlug;
    let i = 2;
    while (usedNames.has(name)) name = `${baseSlug}-${i++}`;
    usedNames.add(name);
    const cfg: Record<string, unknown> =
      backend === "git" ? { url: row.url, branch: row.ref ?? "main", authHint: "ssh" } : {};
    const now = new Date().toISOString();
    stmts.push({
      sql: `INSERT INTO libraries
       (id, name, path, backend, backend_config, ownership, last_sync_at, created_at, updated_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      args: [
        row.id,
        name,
        row.path ?? row.url,
        backend,
        JSON.stringify(cfg),
        ownership,
        row.last_synced_at,
        row.created_at,
        now,
      ],
    });
  }

  // 3. Add the two indexes now that names are unique.
  stmts.push({ sql: `CREATE UNIQUE INDEX idx_libraries_name ON libraries(name);` });
  stmts.push({
    sql: `CREATE UNIQUE INDEX idx_libraries_default
           ON libraries(is_default) WHERE is_default = 1;`,
  });

  // 4. Rename FK column on asset_definitions: source_id -> library_id.
  //    SQLite supports ALTER TABLE ... RENAME COLUMN since 3.25.
  stmts.push({ sql: `ALTER TABLE asset_definitions RENAME COLUMN source_id TO library_id;` });
  stmts.push({
    sql: `CREATE INDEX IF NOT EXISTS idx_asset_defs_library_id
           ON asset_definitions(library_id);`,
  });

  // 5. Drop the legacy sources table.
  stmts.push({ sql: `DROP TABLE sources;` });

  // Apply atomically — libsql `batch` runs the whole set in a transaction.
  await client.batch(stmts);
}

// ---------------------------------------------------------------------------
// Migration — runs only if the legacy `subscriptions` table is present and no
// `assignments` table exists yet (2026-06-08 rename). Idempotent: opens skip it.
// ---------------------------------------------------------------------------

async function legacySubscriptionsPresent(client: Client): Promise<boolean> {
  const rs = await client.execute(
    "SELECT name FROM sqlite_master WHERE type='table' AND name='subscriptions'",
  );
  return rs.rows.length > 0;
}

async function assignmentsPresent(client: Client): Promise<boolean> {
  const rs = await client.execute(
    "SELECT name FROM sqlite_master WHERE type='table' AND name='assignments'",
  );
  return rs.rows.length > 0;
}

/**
 * One-shot `subscriptions` → `assignments` migration. Renames the table and
 * the `subscribed_at` column, drops the old indexes (the DDL's
 * CREATE...IF NOT EXISTS recreates them with the new names). Applied as a
 * single `client.batch` so a partial failure rolls back cleanly.
 */
async function runAssignmentsMigration(client: Client): Promise<void> {
  await client.batch([
    { sql: "ALTER TABLE subscriptions RENAME TO assignments;" },
    { sql: "ALTER TABLE assignments RENAME COLUMN subscribed_at TO assigned_at;" },
    { sql: "DROP INDEX IF EXISTS idx_subs_ws_instance;" },
    { sql: "DROP INDEX IF EXISTS idx_subs_instance_id;" },
  ]);
}

// ---------------------------------------------------------------------------
// Stale FK heal — the `sources → libraries` migration renamed the FK column
// (`source_id → library_id`) with `ALTER TABLE ... RENAME COLUMN`, but SQLite
// leaves the FK's *referenced* table unchanged. After `DROP TABLE sources` the
// FK points at a table that no longer exists, so any insert carrying a non-null
// `library_id` fails under `PRAGMA foreign_keys = ON` with
// `SQLITE_ERROR: no such table: main.sources`. SQLite can't ALTER a FK in place,
// so we rebuild `asset_definitions` with the correct FK when (and only when) the
// stale reference is detected. Idempotent: a healthy table is left untouched.
// ---------------------------------------------------------------------------

// IMPORTANT: keep this in sync with the canonical `asset_definitions` DDL near
// the top of this file (`CREATE TABLE IF NOT EXISTS asset_definitions`). The
// rebuild below copies columns explicitly, so a column added there but not here
// would be silently dropped on the heal path — `healStaleAssetDefinitionsFk`
// guards against that with a runtime parity check on `ASSET_DEFINITIONS_COLUMNS`.
const ASSET_DEFINITIONS_NEW_DDL = `
CREATE TABLE asset_definitions_new (
  id                    TEXT PRIMARY KEY,
  kind                  TEXT NOT NULL,
  publisher             TEXT NOT NULL,
  name                  TEXT NOT NULL,
  version               TEXT NOT NULL,
  sha256                TEXT,
  description           TEXT,
  license               TEXT,
  category              TEXT,
  manifest              TEXT NOT NULL,
  library_id            TEXT REFERENCES libraries(id) ON DELETE SET NULL,
  soft_deps             TEXT,
  kind_provider_version TEXT,
  cached_at             TEXT NOT NULL,
  updated_at            TEXT NOT NULL
)`;

/** Column names of `asset_definitions`, single-sourced for the rebuild copy + the parity guard. */
const ASSET_DEFINITIONS_COLUMNS = [
  "id",
  "kind",
  "publisher",
  "name",
  "version",
  "sha256",
  "description",
  "license",
  "category",
  "manifest",
  "library_id",
  "soft_deps",
  "kind_provider_version",
  "cached_at",
  "updated_at",
] as const;

/** True iff `asset_definitions`'s `library_id` FK references any table other than `libraries`. */
async function assetDefsFkIsStale(client: Client): Promise<boolean> {
  const present = await client.execute(
    "SELECT name FROM sqlite_master WHERE type='table' AND name='asset_definitions'",
  );
  if (present.rows.length === 0) return false;
  const fks = await client.execute("PRAGMA foreign_key_list(asset_definitions)");
  return fks.rows.some((r) => String((r as Record<string, unknown>).table) !== "libraries");
}

/**
 * Rebuild `asset_definitions` so its `library_id` FK references `libraries`.
 *
 * Runs the canonical SQLite "rebuild to change a constraint" sequence. FK
 * enforcement is toggled off *outside* the transaction (SQLite ignores the
 * pragma mid-transaction) so the `INSERT … SELECT` copy doesn't re-validate the
 * about-to-be-replaced stale FK. Existing rows (including any orphaned
 * `library_id` values) are preserved verbatim.
 */
async function healStaleAssetDefinitionsFk(client: Client): Promise<void> {
  if (!(await assetDefsFkIsStale(client))) return;

  // Drift guard: the rebuild copies columns explicitly (below), so a column
  // present on the live table but missing from `ASSET_DEFINITIONS_COLUMNS` would
  // be silently dropped. Fail loudly instead of losing data.
  const info = await client.execute("PRAGMA table_info(asset_definitions)");
  const liveCols = info.rows.map((r) => String((r as Record<string, unknown>).name));
  const dropped = liveCols.filter((c) => !ASSET_DEFINITIONS_COLUMNS.includes(c as never));
  if (dropped.length > 0) {
    throw new Error(
      `asset_definitions FK-heal would drop column(s) [${dropped.join(", ")}] — ` +
        `update ASSET_DEFINITIONS_NEW_DDL + ASSET_DEFINITIONS_COLUMNS in db.ts to match the schema.`,
    );
  }
  const cols = ASSET_DEFINITIONS_COLUMNS.join(", ");

  await client.execute("PRAGMA foreign_keys = OFF");
  try {
    await client.batch([
      ASSET_DEFINITIONS_NEW_DDL,
      `INSERT INTO asset_definitions_new (${cols}) SELECT ${cols} FROM asset_definitions`,
      "DROP TABLE asset_definitions",
      "ALTER TABLE asset_definitions_new RENAME TO asset_definitions",
      "CREATE INDEX IF NOT EXISTS idx_asset_defs_kind ON asset_definitions(kind)",
      "CREATE INDEX IF NOT EXISTS idx_asset_defs_publisher ON asset_definitions(publisher)",
      "CREATE INDEX IF NOT EXISTS idx_asset_defs_library_id ON asset_definitions(library_id)",
    ]);
  } finally {
    await client.execute("PRAGMA foreign_keys = ON");
  }
}

// ---------------------------------------------------------------------------
// Legacy path guard — the index db has lived at three different paths:
//   1. `~/.skaile/library/lib.db`    (pre-2026-05-13)
//   2. `~/.skaile/libraries/lib.db`  (2026-05-13 Libraries redesign)
//   3. `~/.skaile/index.db`          (2026-05-27 vocabulary cleanup)
// The one-shot relocation from 1/2 → 3 ran for one release; this release hard-
// fails with an actionable message instead of silently moving data.
// ---------------------------------------------------------------------------

function assertNoLegacyLibDb(newDbPath: string): void {
  if (fs.existsSync(newDbPath)) return;

  const parent = path.dirname(newDbPath);
  const skaileHome = path.join(os.homedir(), ".skaile");
  const defaultIndexPath = path.join(skaileHome, "index.db");

  const candidates: string[] = [];
  if (path.basename(newDbPath) === "index.db") {
    candidates.push(path.join(parent, "lib.db"));
  }
  if (newDbPath === defaultIndexPath) {
    candidates.push(path.join(skaileHome, "libraries", "lib.db"));
    candidates.push(path.join(skaileHome, "library", "lib.db"));
  }
  if (path.basename(parent) === "libraries") {
    candidates.push(path.join(path.dirname(parent), "library", "lib.db"));
  }

  for (const legacy of candidates) {
    if (!fs.existsSync(legacy)) continue;
    throw new Error(
      `Found legacy index database at ${legacy}. The lib.db → index.db ` +
        `relocation ran for one release and has been removed. Move the file ` +
        `manually to ${newDbPath} (and any -wal / -shm siblings), then retry.`,
    );
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export type LibraryDb = LibSQLDatabase<typeof schema>;

export interface LibraryDbHandle {
  db: LibraryDb;
  close(): void;
}

/**
 * Open (or create) the local library SQLite database.
 *
 * Async because `@libsql/client` is async-only. `LocalIndex` opens the DB
 * lazily on first use to keep its constructor synchronous.
 *
 * @param dbPath - Absolute path to the `index.db` file. Pre-2026-05-27
 *   `lib.db` paths now hard-fail; see {@link assertNoLegacyLibDb}.
 * @returns A handle carrying the Drizzle DB and a synchronous `close()`.
 */
export async function createLibraryDb(dbPath: string): Promise<LibraryDbHandle> {
  assertNoLegacyLibDb(dbPath);
  // Idempotent one-shot: relocate any pre-2026-05-27 sidecars under
  // ~/.skaile/sidecars/<slug>/ into the new manifest catalog at
  // ~/.skaile/store/manifests/. Cheap on subsequent opens (single existsSync).
  try {
    migrateSidecarsToStoreIfNeeded();
  } catch (err) {
    process.stderr.write(
      `[skaile/store] sidecar migration skipped: ${err instanceof Error ? err.message : String(err)}\n`,
    );
  }
  const client = createClient({ url: `file:${dbPath}` });
  await client.execute("PRAGMA journal_mode = WAL");
  await client.execute("PRAGMA foreign_keys = ON");

  // Run the one-shot migration only when needed.
  if ((await legacySourcesPresent(client)) && !(await librariesPresent(client))) {
    await runLibrariesMigration(client);
  }

  // Pre-2026-06-08 DBs still have the legacy `subscriptions` table — rename it.
  if ((await legacySubscriptionsPresent(client)) && !(await assignmentsPresent(client))) {
    await runAssignmentsMigration(client);
  }

  await client.executeMultiple(DDL);

  // Idempotent column add for DBs created before body-link soft deps were
  // persisted — `CREATE TABLE IF NOT EXISTS` is a no-op on an existing
  // `asset_definitions`, so an older DB would miss `soft_deps`. Runs BEFORE the
  // FK heal so its `INSERT … SELECT soft_deps` copy finds the column.
  try {
    await client.execute("ALTER TABLE asset_definitions ADD COLUMN soft_deps TEXT");
  } catch (err) {
    if (!/duplicate column/i.test(err instanceof Error ? err.message : String(err))) {
      throw err;
    }
  }

  // Heal a stale `asset_definitions` FK left by an older `sources → libraries`
  // migration (FK still references the dropped `sources` table). No-op when the
  // FK is already correct, so greenfield + already-healed DBs pay one PRAGMA.
  await healStaleAssetDefinitionsFk(client);

  // 2026-05-27 vocabulary cleanup: the `store` backend was retired. Refuse to
  // open if any leftover row still claims that backend — the user needs to
  // convert it to a git-backed library via `skaile library link <git-url>`
  // before continuing.
  if (await librariesPresent(client)) {
    const storeRows = await client.execute({
      sql: "SELECT id, name FROM libraries WHERE backend = ?",
      args: ["store"],
    });
    if (storeRows.rows.length > 0) {
      const names = storeRows.rows
        .map((r) => String((r as Record<string, unknown>).name))
        .join(", ");
      client.close();
      throw new Error(
        `The 'store' library backend was removed in the 2026-05-27 vocabulary ` +
          `cleanup. Found rows still using it: ${names}. ` +
          `Run \`skaile library link <git-url>\` to convert each, or remove them ` +
          `with \`skaile library remove <name>\`.`,
      );
    }
  }

  // Idempotent column add for DBs created before the pointer-only pivot —
  // `CREATE TABLE IF NOT EXISTS` above is a no-op on an existing `instances`
  // table, so an older DB would miss `source_commit_sha`. Swallow the
  // "duplicate column" error so re-opens are clean.
  try {
    await client.execute("ALTER TABLE instances ADD COLUMN source_commit_sha TEXT");
  } catch (err) {
    if (!/duplicate column/i.test(err instanceof Error ? err.message : String(err))) {
      throw err;
    }
  }

  const db = drizzle(client, { schema });
  return { db, close: () => client.close() };
}
